import { extendObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ZAttrMeta, ZListDef } from "src/business/attribute";
import {
  ZAttrValueListRaw,
  zAttrValueListRaw,
  ZAttrValueRecord,
} from "src/business/attribute/ZAttrValue";
import { delay } from "src/common/delay";

import { MfAttrListCollapsible } from "../components/MfAttrListCollapsible";
import { MfPath } from "../MFormTypes";
import { ZMfNode, ZMfNodeAttr } from "../ZMfNode";
import { createMfNode } from ".";
import { createMfNodeBase } from "./createMfNodeBase";
import { MfNode, PropsRender } from "./MfNode";
import { MfNodeAttr } from "./MfNodeAttr";

export interface MfNodeAttrList extends MfNodeAttr {
  readonly value: ZAttrValueListRaw;
  collapsed: boolean;
  setCollapsed(value: boolean): void;
  idCounter: number;
  generateId(): string;
  add(): void;
  deleteRecord(key: string): void;
  recordNodes: Record<string, MfNode>;
  collapsedRecords: Set<string>;
  setCollapsedRecord(key: string | "all", collapsed: boolean): void;
}

const makeRowsCompositionKey = (rows: Record<string, MfNode>): string =>
  Object.keys(rows).sort().join(",");

export const createMfNodeAttrList = (
  meta: ZMfNodeAttr,
  ownerDataPath: MfPath,
  typeDef: ZListDef,
): MfNodeAttrList => {
  const fieldsMeta = typeDef.fields ?? [];
  const attrName = meta.attr.name;
  const dataPath = [...ownerDataPath, attrName];
  const createRecordNode = (key: string) =>
    createMfListItem(key, fieldsMeta, dataPath);
  const ctrl: MfNodeAttrList = extendObservable(createMfNodeBase(meta), {
    meta,
    Render: observer(({ ctx }: PropsRender) => (
      <MfAttrListCollapsible attrMeta={meta.attr} node={ctrl} ctx={ctx} />
    )),
    collapsed: false,
    setCollapsed(value: boolean) {
      this.collapsed = value;
    },
    collapsedRecords: new Set<string>(),
    setCollapsedRecord(key: string | "all", collapsed: boolean) {
      if (collapsed) {
        if (key === "all") {
          this.items?.forEach((it) => this.collapsedRecords.add(it.key));
        } else {
          this.collapsedRecords.add(key);
        }
      } else if (key === "all") {
        this.collapsedRecords.clear();
      } else {
        this.collapsedRecords.delete(key);
      }
    },
    attrValue: null,
    initialValue: null,
    setValue(newValue: unknown) {
      const res = zAttrValueListRaw.safeParse(newValue);
      const newRecords = res.data ?? {};
      const { recordNodes } = ctrl;
      // Удалить ненужные строки и обновить существующие
      Object.entries(recordNodes).forEach(([key, node]) => {
        const recordValue = newRecords[key];
        if (recordValue) {
          node.setValue(recordValue);
        } else {
          delete recordNodes[key];
        }
      });
      // Добавить новые
      Object.entries(newRecords).forEach(([key, value]) => {
        if (!(key in recordNodes)) {
          const recNode = createRecordNode(key);
          recNode.setValue(value);
          recordNodes[key] = recNode;
        }
      });
      this.recordNodes = recordNodes;
    },
    get value() {
      const result: ZAttrValueListRaw = {};
      Object.entries(this.recordNodes).forEach(([key, node]) => {
        if (typeof node.value === "object") {
          result[key] = node.value as ZAttrValueRecord;
        }
      });
      return result;
    },
    get key() {
      return attrName;
    },
    get dataKey() {
      return attrName;
    },
    dataPath,
    get title() {
      return meta.attr.title;
    },
    // Была бурная дискуссия насчет проверки минимального количества записей.
    // Технических сложностей с этим нет. Но бизнес не готов сформулировать требования/пожелания.
    // Постановили: пока не делать никаких проверок
    validators: [],
    // Для этого компонента по дефолту используем всю ширину
    colSpan: meta.attr.edit?.colSpan ?? "*",
    idCounter: 0,
    generateId() {
      return `#${++this.idCounter}`;
    },
    add() {
      const newValue = ctrl.value ? JSON.parse(JSON.stringify(ctrl.value)) : {};
      const row: ZAttrValueRecord = {};
      fieldsMeta.forEach(({ name }) => {
        row[name] = null;
      });
      newValue[ctrl.generateId()] = row;
      ctrl.setValue(newValue);
    },
    deleteRecord(key: string) {
      const newValue = ctrl.value ? JSON.parse(JSON.stringify(ctrl.value)) : {};
      delete newValue[key];
      ctrl.setValue(newValue);
    },
    recordNodes: {} as Record<string, MfNode>,
    get items(): MfNode[] | undefined {
      return Object.values(this.recordNodes);
    },
    async activate(fieldKey: string) {
      if (ctrl.collapsed) {
        ctrl.setCollapsed(false);
        await delay(50);
      }
      if (ctrl.collapsedRecords.has(fieldKey)) {
        ctrl.setCollapsedRecord(fieldKey, false);
        await delay(50);
      }
    },
    compositionKey: "",
    get isChanged(): boolean {
      // Если не совпадают ключи строк, то изменения точно есть
      const currentKey = makeRowsCompositionKey(this.recordNodes);
      if (currentKey !== this.compositionKey) {
        return true;
      }

      // Если состав строк не менялся, тогда проверить изменения в строках
      return this.items?.some(({ isChanged }) => isChanged) ?? false;
    },
    setAsUnchanged() {
      this.compositionKey = makeRowsCompositionKey(this.recordNodes);
      this.items?.forEach((it) => {
        it.setAsUnchanged();
      });
    },
  });
  return ctrl;
};

const createMfListItem = (
  key: string,
  fieldsMeta: ZAttrMeta[],
  ownerDataPath: MfPath,
): MfNode => {
  const meta: ZMfNode = {
    type: "grid",
    nodes: fieldsMeta.map((attr) => ({
      type: "attr",
      attr,
    })),
  };
  const dataPath = [...ownerDataPath, key];
  const content = createMfNode(meta, dataPath);
  const ctrl: MfNode = extendObservable(createMfNodeBase({}), {
    meta, // Использованы одинаковые метаданные для записи и content
    key,
    dataKey: key,
    dataPath,
    items: [content],
    get value() {
      return content.value;
    },
    setValue(newValue: unknown) {
      content.setValue(newValue);
    },
    Render: (props: PropsRender) => <content.Render {...props} />,
    get isChanged(): boolean {
      return content.isChanged;
    },
    setAsUnchanged() {
      content.setAsUnchanged();
    },
  });
  return ctrl;
};
